import React, { useEffect, useState, useRef, useContext, useCallback } from "react";
import { AuthContext } from "utils/AuthContext";
import { FlexCenter, FlexCenterCol, DayStyles } from "styles/global-style";
import useKey from "utils/KeyListener";

import arrow from "imgs/arrow-right.png";
import xmark from "imgs/xmark.png";

function formatLeaveDate(date) {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

export default function DaySelect({ show, clickCb, closeCb, leaveProjectCb, canLeave, leaveDate }) {
  const { user, supabase, roleAccess } = useContext(AuthContext);

  const buttonWidthRef = useRef();
  const otherActionsRef = useRef();

  const [data, setData] = useState();
  const [openOtherActions, setOpenOtherActions] = useState(false);

  //xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx

  const fetchData = useCallback(async () => {
    const res = await supabase.from("day_types").select().eq("org_id", user.org.id).order("id");
    const longestName = res?.data?.reduce((acc, curr) => (acc < curr.name.length ? curr.name.length : acc), 0);
    buttonWidthRef.current = longestName * 4;
    setData(res?.data);
  }, [supabase, user]);

  //xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx

  useKey("Escape", false, () => {
    if (openOtherActions) setOpenOtherActions(false);
    else if (closeCb) closeCb();
  });

  useEffect(() => {
    if (!openOtherActions) return;
    const handleClickOutside = (e) => {
      if (otherActionsRef.current && !otherActionsRef.current.contains(e.target)) setOpenOtherActions(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [openOtherActions]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div style={{ position: "absolute", height: 100, width: "100%", top: show ? 0 : -150, background: "white", zIndex: 10000, transition: "all 150ms ease-out", display: "flex", alignItems: "center", justifyContent: "center", paddingTop: 18, paddingBottom: 18, boxSizing: "border-box" }}>
      {data && (
        <>
          <FlexCenter style={{ alignItems: "flex-start" }}>
            <DayLarge icon={DayStyles.selected.icon} color={DayStyles.selected.color} />
            <img alt="arrow" src={arrow} style={{ height: 20, opacity: 0.3, padding: "0px 20px 14px 20px", marginTop: 9 }} />

            {data.map((type, i) => {
              if (!roleAccess(type.role_required)) return <div key={"daytype" + i} />;
              const icon = DayStyles[type.style]?.icon || null;
              const color = DayStyles[type.style]?.color || null;
              const border = DayStyles[type.style]?.borderSelector || DayStyles[type.style]?.border || null;
              return (
                <DayLarge
                  key={"daytype" + i}
                  icon={icon}
                  color={color}
                  border={border}
                  name={type.name}
                  width={buttonWidthRef.current}
                  callback={() => clickCb(type.id, type.style)}
                />
              );
            })}
          </FlexCenter>

          <div style={{ position: "absolute", right: 20, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", gap: 12 }}>
            {canLeave && leaveProjectCb && (
              <div ref={otherActionsRef} style={{ position: "relative", paddingRight: 30 }}>
                <button
                  type="button"
                  onClick={() => setOpenOtherActions((o) => !o)}
                  style={{
                    padding: "6px 12px",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: "#888",
                    background: "white",
                    border: "1px solid #aaa",
                    borderRadius: 8,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  Other actions
                </button>
                {openOtherActions && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      right: 0,
                      marginTop: 4,
                      minWidth: "100%",
                      background: "white",
                      border: "1px solid #aaa",
                      borderRadius: 8,
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      overflow: "hidden",
                      zIndex: 10001,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        leaveProjectCb();
                        setOpenOtherActions(false);
                      }}
                      style={{
                        display: "block",
                        width: "100%",
                        padding: "8px 12px",
                        fontSize: "0.7rem",
                        fontWeight: 500,
                        color: "#333",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        textAlign: "left",
                        whiteSpace: "nowrap",
                      }}
                      onMouseOver={(e) => (e.currentTarget.style.background = "#f0f0f0")}
                      onMouseOut={(e) => (e.currentTarget.style.background = "none")}
                    >
                      Leaves project{leaveDate ? ` on ${formatLeaveDate(leaveDate)}` : ""}
                    </button>
                  </div>
                )}
              </div>
            )}
            <img alt="close" src={xmark} style={{ height: 20, opacity: 0.3, cursor: "pointer" }} onClick={closeCb} />
          </div>
        </>
      )}
    </div>
  );
}

const DayLarge = ({ color, border, icon, name, width, callback }) => {
  const [hover, setHover] = useState();
  return (
    <div style={{ width: width, cursor: "pointer" }} onMouseOver={() => setHover(true)} onMouseOut={() => setHover(false)} onClick={() => (callback ? callback() : null)}>
      <FlexCenterCol gap={2}>
        <div
          style={{
            height: 38,
            width: 38,
            borderRadius: 5,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            background: color,
            border: border,
            boxSizing: "border-box",
            transform: hover ? "scale(1.08)" : "scale(1)",
          }}
        >
          {icon && <img alt="daytypeicon" src={icon} style={{ height: "40%", opacity: 0.3, userSelect: "none" }} />}
        </div>
        <div style={{ fontSize: "0.6rem", fontWeight: 600, color: "gray", height: 15, textAlign: "center", userSelect: "none", cursor: "pointer" }}>{name?.toUpperCase()}</div>
      </FlexCenterCol>
    </div>
  );
};

